import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAutoLogout from '../hooks/useAutoLogout';
import API_BASE from '../config';
import { Shield, FileDown, Trash2, Eye, Edit3, CheckCircle, AlertCircle, ChevronRight, Send } from 'lucide-react';
import Toast from '../components/Toast';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

// infoCards is defined inside the component below (needs setCorrectionOpen in scope)
const baseInfoCards = [
  {
    icon: Eye,
    title: 'Right to Access',
    desc: 'Request a complete export of all personal data we hold about you.',
    iconBg: 'rgba(59,130,246,0.12)',
    iconColor: '#60a5fa',
    accent: '#3b82f6',
  },
  {
    icon: Edit3,
    title: 'Right to Correction',
    desc: 'Ask us to correct any inaccurate or incomplete personal data. Tap to submit a request.',
    iconBg: 'rgba(245,158,11,0.12)',
    iconColor: '#fbbf24',
    accent: '#f59e0b',
    key: 'correction',
  },
  {
    icon: Trash2,
    title: 'Right to Erasure',
    desc: 'Request permanent deletion of your personal data from our systems.',
    iconBg: 'rgba(239,68,68,0.12)',
    iconColor: '#f87171',
    accent: '#ef4444',
  },
];

const howItWorksSteps = [
  { step: '01', text: 'Enter your Profile ID in the form' },
  { step: '02', text: 'Choose to export your data or request erasure' },
  { step: '03', text: 'We process your request within 30 days' },
  { step: '04', text: 'All requests are logged and auditable' },
];

export default function DataRights() {
  const navigate = useNavigate();
  useAutoLogout();
  const isMobile = useIsMobile();

  const [profileId, setProfileId] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [eraseLoading, setEraseLoading] = useState(false);
  const [correctLoading, setCorrectLoading] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const infoCards = baseInfoCards.map(c =>
    c.key === 'correction' ? { ...c, action: setCorrectionOpen } : c
  );

  const isLight = (localStorage.getItem('martech_theme') || 'dark') === 'light';
  const T = {
    bg: isLight ? '#f8fafc' : '#050505',
    card: isLight ? '#ffffff' : '#0d0d0d',
    cardHover: isLight ? '#f1f5f9' : '#111111',
    border: isLight ? '#e2e8f0' : '#1a1a1a',
    borderHover: isLight ? '#cbd5e1' : '#2a2a2a',
    text: isLight ? '#0f172a' : '#f4f4f5',
    muted: isLight ? '#64748b' : '#71717a',
    subtle: isLight ? '#94a3b8' : '#3f3f46',
    input: isLight ? '#f8fafc' : '#080808',
    inputBorder: isLight ? '#e2e8f0' : '#1e1e1e',
  };

  useEffect(() => {
    if (!localStorage.getItem('martech_token') && !sessionStorage.getItem('token')) {
      navigate('/login');
    }
  }, [navigate]);

  const showToast = (msg, variant = 'green') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3100);
  };

  // endpoint: 'export' -> GET /export/:id, 'erase' -> DELETE /erase/:id
  // (methods must match routes/dpdp.js exactly — export reads, erase deletes)
  const callApi = async (endpoint, setLoading) => {
    if (!profileId.trim()) {
      setError('Please enter a Profile ID.');
      return;
    }
    setSuccess(''); setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('martech_token') || sessionStorage.getItem('token');
      const method = endpoint === 'export' ? 'GET' : 'DELETE';
      const res = await fetch(`${API_BASE}/api/v1/dpdp/${endpoint}/${encodeURIComponent(profileId.trim())}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data));
        showToast(endpoint === 'export' ? 'Data export retrieved successfully' : 'Profile erased successfully', 'green');
      } else {
        setError(data.message || 'Request failed. Please try again.');
        showToast('Request failed', 'red');
      }
    } catch {
      setError('Unable to connect. Please try again.');
      showToast('Network error', 'red');
    } finally {
      setLoading(false);
    }
  };

  // Correction request -> POST /api/v1/dpdp/rights-request
  // (this is a request to a human reviewer, not an automatic data mutation)
  const submitCorrection = async () => {
    if (!profileId.trim()) {
      setError('Please enter a Profile ID.');
      return;
    }
    if (!correctionReason.trim()) {
      setError('Please describe what needs to be corrected.');
      return;
    }
    setSuccess(''); setError('');
    setCorrectLoading(true);
    try {
      const token = localStorage.getItem('martech_token') || sessionStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/v1/dpdp/rights-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId.trim(),
          request_type: 'correction',
          details: correctionReason.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(JSON.stringify(data, null, 2));
        setCorrectionOpen(false);
        setCorrectionReason('');
        showToast('Correction request submitted', 'green');
      } else {
        setError(data.message || 'Request failed. Please try again.');
        showToast('Request failed', 'red');
      }
    } catch {
      setError('Unable to connect. Please try again.');
      showToast('Network error', 'red');
    } finally {
      setCorrectLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, transition: 'background 300ms ease' }}>
      <div className="h-14 md:hidden" />
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main style={{
        padding: isMobile ? '20px 14px 40px' : '32px 28px 56px',
        maxWidth: 1200, margin: '0 auto',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          marginBottom: isMobile ? 22 : 32,
          flexDirection: isMobile ? 'column' : 'row',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: isMobile ? 40 : 44, height: isMobile ? 40 : 44, borderRadius: 14, flexShrink: 0,
              background: 'rgba(124,58,237,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Shield size={isMobile ? 19 : 22} color="#a78bfa" />
            </div>
            <div>
              <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: T.text, margin: 0, letterSpacing: '-0.3px' }}>
                Data Rights Portal
              </h1>
              <p style={{ color: T.muted, fontSize: isMobile ? 12 : 13, marginTop: 3 }}>
                Manage your personal data under DPDP Act 2023
              </p>
            </div>
          </div>
          <span style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 999,
            background: 'rgba(16,185,129,0.12)',
            border: '1px solid rgba(16,185,129,0.2)',
            color: '#10b981', fontSize: isMobile ? 11 : 12, fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>
            <CheckCircle size={13} />
            DPDP Act 2023 Compliant
          </span>
        </div>

        {/* Info cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 10 : 14,
          marginBottom: isMobile ? 20 : 28,
        }}>
          {infoCards.map(({ icon: Icon, title, desc, iconBg, iconColor, accent, action }, i) => (
            <div
              key={title}
              onMouseEnter={() => !isMobile && setHoveredCard(i)}
              onMouseLeave={() => !isMobile && setHoveredCard(null)}
              onClick={action ? () => action(true) : undefined}
              style={{
                background: hoveredCard === i ? T.cardHover : T.card,
                border: `1px solid ${hoveredCard === i ? T.borderHover : T.border}`,
                borderRadius: 16, padding: isMobile ? 18 : 24,
                transition: 'all 200ms ease',
                transform: hoveredCard === i && !isMobile ? 'translateY(-2px)' : 'none',
                boxShadow: hoveredCard === i && !isMobile ? '0 8px 24px rgba(0,0,0,0.15)' : 'none',
                display: isMobile ? 'flex' : 'block',
                alignItems: isMobile ? 'center' : 'stretch',
                gap: isMobile ? 14 : 0,
                cursor: action ? 'pointer' : 'default',
              }}
            >
              <div style={{
                width: isMobile ? 38 : 42, height: isMobile ? 38 : 42, borderRadius: 12, flexShrink: 0,
                background: iconBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: isMobile ? 0 : 16,
              }}>
                <Icon size={isMobile ? 18 : 20} color={iconColor} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: T.text, margin: isMobile ? '0 0 3px' : '0 0 8px' }}>{title}</h3>
                <p style={{ fontSize: isMobile ? 12 : 13, color: T.muted, lineHeight: 1.5, margin: 0 }}>{desc}</p>
                {!isMobile && (
                  <div style={{ marginTop: 16, height: 3, borderRadius: 99, background: T.border }}>
                    <div style={{ height: 3, borderRadius: 99, width: hoveredCard === i ? '100%' : '0%', background: accent, transition: 'width 300ms ease' }} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Form + Result grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 14 : 16,
          alignItems: 'start',
        }}>

          {/* Submit form */}
          <div style={{
            background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: isMobile ? 20 : 28,
          }}>
            <h2 style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
              Submit a Data Request
            </h2>
            <p style={{ fontSize: isMobile ? 12 : 13, color: T.muted, marginBottom: isMobile ? 18 : 24 }}>
              Enter a Profile ID to export data or request erasure.
            </p>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Profile ID
            </label>
            <input
              type="text"
              value={profileId}
              onChange={e => setProfileId(e.target.value)}
              placeholder="e.g. cust_8f3a9b2e…"
              style={{
                width: '100%', padding: isMobile ? '12px 14px' : '11px 14px',
                background: T.input, border: `1px solid ${T.inputBorder}`,
                borderRadius: 10, color: T.text,
                fontSize: isMobile ? 16 : 13, outline: 'none',
                marginBottom: isMobile ? 16 : 20, boxSizing: 'border-box',
                transition: 'border-color 150ms ease',
              }}
              onFocus={e => e.target.style.borderColor = '#7c3aed'}
              onBlur={e => e.target.style.borderColor = T.inputBorder}
            />

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
              <button
                onClick={() => callApi('export', setExportLoading)}
                disabled={exportLoading || eraseLoading}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: isMobile ? '13px 16px' : '11px 16px', borderRadius: 10,
                  border: '1px solid rgba(59,130,246,0.25)',
                  background: 'rgba(59,130,246,0.12)', color: '#60a5fa',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: exportLoading || eraseLoading ? 0.5 : 1,
                  transition: 'all 200ms ease',
                  minHeight: 42, boxSizing: 'border-box',
                }}
                onMouseEnter={e => { if (!isMobile && !exportLoading && !eraseLoading) e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
                onMouseLeave={e => { if (!isMobile) e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
              >
                {exportLoading
                  ? <span style={{ width: 14, height: 14, border: '2px solid rgba(96,165,250,0.3)', borderTopColor: '#60a5fa', borderRadius: '50%', display: 'inline-block' }} className="animate-spin" />
                  : <FileDown size={15} />
                }
                Export My Data
              </button>

              <button
                onClick={() => callApi('erase', setEraseLoading)}
                disabled={exportLoading || eraseLoading}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: isMobile ? '13px 16px' : '11px 16px', borderRadius: 10,
                  border: '1px solid rgba(239,68,68,0.25)',
                  background: 'rgba(239,68,68,0.10)', color: '#f87171',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: exportLoading || eraseLoading ? 0.5 : 1,
                  transition: 'all 200ms ease',
                  minHeight: 42, boxSizing: 'border-box',
                }}
                onMouseEnter={e => { if (!isMobile && !exportLoading && !eraseLoading) e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; }}
                onMouseLeave={e => { if (!isMobile) e.currentTarget.style.background = 'rgba(239,68,68,0.10)'; }}
              >
                {eraseLoading
                  ? <span style={{ width: 14, height: 14, border: '2px solid rgba(248,113,113,0.3)', borderTopColor: '#f87171', borderRadius: '50%', display: 'inline-block' }} className="animate-spin" />
                  : <Trash2 size={15} />
                }
                Request Erasure
              </button>
            </div>

            {/* Correction toggle */}
            <button
              onClick={() => setCorrectionOpen(v => !v)}
              style={{
                marginTop: 10, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: isMobile ? '13px 16px' : '11px 16px', borderRadius: 10,
                border: `1px solid ${correctionOpen ? 'rgba(245,158,11,0.35)' : T.border}`,
                background: correctionOpen ? 'rgba(245,158,11,0.10)' : 'transparent',
                color: correctionOpen ? '#fbbf24' : T.muted,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 200ms ease', minHeight: 42, boxSizing: 'border-box',
              }}
            >
              <Edit3 size={15} />
              Request Correction
            </button>

            {correctionOpen && (
              <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  What needs to be corrected?
                </label>
                <textarea
                  value={correctionReason}
                  onChange={e => setCorrectionReason(e.target.value)}
                  placeholder="e.g. My phone number on file is outdated, please update it to..."
                  rows={3}
                  style={{
                    width: '100%', padding: '11px 14px',
                    background: T.input, border: `1px solid ${T.inputBorder}`,
                    borderRadius: 10, color: T.text,
                    fontSize: isMobile ? 16 : 13, outline: 'none',
                    marginBottom: 12, boxSizing: 'border-box',
                    resize: 'vertical', fontFamily: 'inherit',
                  }}
                  onFocus={e => e.target.style.borderColor = '#f59e0b'}
                  onBlur={e => e.target.style.borderColor = T.inputBorder}
                />
                <button
                  onClick={submitCorrection}
                  disabled={correctLoading}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    padding: isMobile ? '13px 16px' : '11px 16px', borderRadius: 10,
                    border: '1px solid rgba(245,158,11,0.3)',
                    background: 'rgba(245,158,11,0.14)', color: '#fbbf24',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    opacity: correctLoading ? 0.5 : 1,
                    minHeight: 42, boxSizing: 'border-box',
                  }}
                >
                  {correctLoading
                    ? <span style={{ width: 14, height: 14, border: '2px solid rgba(251,191,36,0.3)', borderTopColor: '#fbbf24', borderRadius: '50%', display: 'inline-block' }} className="animate-spin" />
                    : <Send size={14} />
                  }
                  Submit Correction Request
                </button>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{
                marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 16px', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                color: '#f87171', fontSize: 13,
              }}>
                <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                {error}
              </div>
            )}
          </div>

          {/* Result / Info panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 14 }}>
            {success ? (
              <div style={{
                background: T.card, border: '1px solid rgba(16,185,129,0.2)',
                borderRadius: 16, padding: isMobile ? 18 : 24,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <CheckCircle size={18} color="#10b981" />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>Request Submitted</span>
                </div>
                <pre style={{
                  fontSize: 12, color: T.muted,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  fontFamily: 'JetBrains Mono, monospace',
                  background: T.input, padding: 12, borderRadius: 8,
                  border: `1px solid ${T.border}`, margin: 0,
                  maxHeight: isMobile ? 220 : 'none', overflowY: isMobile ? 'auto' : 'visible',
                }}>
                  {success}
                </pre>
              </div>
            ) : isMobile ? (
              /* Mobile: collapsible accordion to save vertical space */
              <div style={{
                background: T.card, border: `1px solid ${T.border}`,
                borderRadius: 16, overflow: 'hidden',
              }}>
                <button
                  onClick={() => setHowItWorksOpen(v => !v)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '16px 18px', minHeight: 44, boxSizing: 'border-box',
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>How it works</span>
                  <ChevronRight size={16} color={T.muted} style={{
                    transform: howItWorksOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 200ms ease',
                  }} />
                </button>
                {howItWorksOpen && (
                  <div style={{ padding: '0 18px 18px' }}>
                    {howItWorksSteps.map(({ step, text }) => (
                      <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                        <span style={{
                          width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                          background: 'rgba(124,58,237,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 800, color: '#a78bfa',
                        }}>{step}</span>
                        <span style={{ fontSize: 12, color: T.muted, paddingTop: 4, lineHeight: 1.5 }}>{text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: T.card, border: `1px solid ${T.border}`,
                borderRadius: 16, padding: 24,
              }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: '0 0 12px' }}>
                  How it works
                </h3>
                {howItWorksSteps.map(({ step, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: 'rgba(124,58,237,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, color: '#a78bfa',
                    }}>{step}</span>
                    <span style={{ fontSize: 13, color: T.muted, paddingTop: 5, lineHeight: 1.5 }}>{text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Compliance note */}
            <div style={{
              background: 'rgba(16,185,129,0.06)',
              border: '1px solid rgba(16,185,129,0.15)',
              borderRadius: 12, padding: isMobile ? 14 : 16,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <Shield size={16} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: isMobile ? 11.5 : 12, color: '#10b981', margin: 0, lineHeight: 1.6 }}>
                We will respond within <strong>30 days</strong> as per DPDP Act 2023. All requests are logged, timestamped, and auditable.
              </p>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}